import type {
  FieldDeadlineStatus,
  FieldReportListItem,
  FieldReportSituation,
  FieldReportSync,
} from '@/modules/field/types';
import { resolveFieldReportDeadlineStatus } from '@/modules/field/utils/fieldReportDeadline';

const SITUATION_ALIASES: Record<string, FieldReportSituation> = {
  draft: 'draft',
  rascunho: 'draft',
  pending: 'pending',
  pendente: 'pending',
  recused: 'recused',
  recusado: 'recused',
  in_approval: 'in_approval',
  'em aprovação': 'in_approval',
  'em aprovacao': 'in_approval',
  approved: 'approved',
  aprovado: 'approved',
  cancelled: 'cancelled',
  cancelado: 'cancelled',
};

function normalizeSituation(
  raw: unknown,
): FieldReportSituation {
  if (typeof raw !== 'string' || raw.trim() === '') {
    return 'pending';
  }

  const key = raw.trim().toLowerCase();

  return SITUATION_ALIASES[key] ?? (key as FieldReportSituation);
}

function normalizeDeadlineStatus(
  raw: unknown,
  reportDateIso: string | null | undefined,
): FieldDeadlineStatus {
  if (typeof reportDateIso === 'string' && reportDateIso !== '') {
    return resolveFieldReportDeadlineStatus(reportDateIso);
  }

  return raw === 'delayed' ? 'delayed' : 'on_time';
}

function normalizeSync(raw: unknown): FieldReportSync {
  return raw === 'synced' ? 'synced' : 'not_synced';
}

const EDITABLE_SITUATIONS: FieldReportSituation[] = [
  'draft',
  'pending',
  'recused',
  'in_approval',
];

const DELETABLE_SITUATIONS: FieldReportSituation[] = ['draft', 'pending', 'in_approval'];

function resolveCanDelete(
  item: Partial<FieldReportListItem> & { status?: string },
  situation: FieldReportSituation,
  type: string,
): boolean {
  if (!DELETABLE_SITUATIONS.includes(situation)) {
    return false;
  }

  const deletableTypes = new Set(['rdo', 'rcp', 'rcp_team', 'rcp_exec']);
  if (!deletableTypes.has(type)) {
    return false;
  }

  // Flag do servidor tem prioridade (RCP Equipe, permissões, etc.).
  if (typeof item.canDelete === 'boolean') {
    return item.canDelete;
  }

  // Cache legado sem canDelete: mantém regra antiga só para RDO.
  if (type !== 'rdo') {
    return false;
  }

  const measurementLocked =
    Boolean(item.measurementPeriodLocked)
    || Boolean(item.measurementPeriodLockMessage);

  if (measurementLocked) {
    return true;
  }

  if (item.isFromCockpit) {
    return false;
  }

  return true;
}

function resolveCanEdit(
  item: Partial<FieldReportListItem> & { status?: string },
  situation: FieldReportSituation,
): boolean {
  if (situation === 'cancelled') {
    return false;
  }

  const measurementBlocksEdit =
    Boolean(item.measurementPeriodLocked)
    && (situation === 'pending' || situation === 'draft');

  if (measurementBlocksEdit) {
    return false;
  }

  if (typeof item.canEdit === 'boolean') {
    return item.canEdit;
  }

  if (
    typeof item.status === 'string' &&
    ['draft', 'pending', 'recused'].includes(item.status)
  ) {
    return true;
  }

  return EDITABLE_SITUATIONS.includes(situation);
}

/** Garante shape mínimo para cards — evita crash com cache legado. */
export function normalizeFieldReport(
  item: Partial<FieldReportListItem> & { id: number; status?: string },
): FieldReportListItem {
  const type = (item.type ?? 'rdo').toLowerCase();
  const situation = normalizeSituation(item.situation ?? item.status);
  const sync = normalizeSync(item.sync);
  const canEdit = resolveCanEdit(item, situation);
  const canDelete = resolveCanDelete(item, situation, type);

  return {
    id: item.id,
    clientUuid: item.clientUuid ?? null,
    date: item.date ?? '—',
    reportDateIso: item.reportDateIso ?? '',
    projectId: item.projectId ?? 0,
    clientId: item.clientId ?? null,
    divisionId: item.divisionId ?? null,
    type,
    client: item.client ?? '—',
    division: item.division ?? '—',
    reportType: item.reportType ?? type.toUpperCase(),
    situation,
    deadlineStatus: normalizeDeadlineStatus(item.deadlineStatus, item.reportDateIso),
    title: item.title ?? `${type.toUpperCase()} #${item.id}`,
    progress:
      typeof item.progress === 'number' && !Number.isNaN(item.progress)
        ? item.progress
        : 0,
    sync,
    createdBy: item.createdBy ?? null,
    collaboratorUserIds: Array.isArray(item.collaboratorUserIds)
      ? item.collaboratorUserIds
      : [],
    activityScheduleItemIds: Array.isArray(item.activityScheduleItemIds)
      ? item.activityScheduleItemIds
      : [],
    canEdit,
    canEvaluate: Boolean(item.canEvaluate),
    canDelete,
    canSync: item.canSync !== undefined ? Boolean(item.canSync) : sync !== 'synced',
    isFromCockpit: Boolean(item.isFromCockpit),
    measurementPeriodLocked: Boolean(item.measurementPeriodLocked),
    measurementPeriodLockMessage:
      typeof item.measurementPeriodLockMessage === 'string'
        ? item.measurementPeriodLockMessage
        : null,
    rejectionJustification:
      typeof item.rejectionJustification === 'string' ? item.rejectionJustification : null,
    rejectedAt: typeof item.rejectedAt === 'string' ? item.rejectedAt : null,
    rejectedByName: typeof item.rejectedByName === 'string' ? item.rejectedByName : null,
    syncError: typeof item.syncError === 'string' ? item.syncError : null,
    managerId:
      typeof item.managerId === 'number' && !Number.isNaN(item.managerId)
        ? item.managerId
        : null,
    managerName: typeof item.managerName === 'string' ? item.managerName : null,
  };
}
